import { TitleCasePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MdcInputChipsComponent } from '../../../components/mdc/mdc-chips/mdc-input-chips.component';
import { MdcDialogComponent } from '../../../components/mdc/mdc-dialog/mdc-dialog.component';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { RepairerJobSubscription } from '../../../model/user.model';
import { HumanizePipe } from '../../../pipes/humanize.pipe';
import { RepairerAdminService } from '../../../services/repairer-admin.service';
import { EmailValidator } from '../../../validators/email.validator';
import { LocationAdminStore } from '../location.admin.store.component';

@Component({
  selector: 'job-notifications-subscribe',
  imports: [
    MatDialogModule,
    MdcDialogComponent,
    MatCheckboxModule,
    HumanizePipe,
    ReactiveFormsModule,
    TitleCasePipe,
    MdcInputChipsComponent
  ],
  template: `
    <mdc-dialog
      dialogTitle="Subscribe to Job Events"
      [closeOnSave]="false"
      [disableSave]="form.invalid"
      (save)="subscribeToEvents()">
      <ng-template #contentTemplate>
        <div class="flex flex-col body-1 -mb-10" [formGroup]="form">
          <span>Select at least one event from the list of events to subscribe to:</span>

          <div class="grid grid-cols-3 gap-x-20 gap-y-15 mt-15 mb-30 ml-10">
            @for (event of eventOptions; track event) {
              <mat-checkbox
                [checked]="emailSubscriptionEvents.value.includes(event)"
                (change)="toggleEvent($event.checked, event)">
                {{ event | humanize | titlecase }}
              </mat-checkbox>
            }
          </div>

          <mdc-input-chips label="E-mail Recipient(s)" formControlName="emailSubscriptionRecipients" />
        </div>
      </ng-template>
    </mdc-dialog>
  `
})
export class JobNotificationsSubscribeComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<JobNotificationsSubscribeComponent>);
  #fb = inject(FormBuilder);
  #locationAdminStore = inject(LocationAdminStore);
  #messagingService = inject(MessagingService);
  #repairerAdminService = inject(RepairerAdminService);

  eventOptions = [
    'REQUEST_FOR_QUOTE',
    'REPAIRS_AUTHORISED',
    'REPAIRS_CANCELLED',
    'SUPPLEMENT_AUTHORISED',
    'REGO_UPDATE',
    'EXCESS_UPDATE',
    'AUTHORITY_REVOKED',
    'MORE_INFORMATION_REQUIRED',
    'QUOTE_ASSESSED',
    'INVOICE_APPROVED'
  ];
  form: FormGroup;

  ngOnInit(): void {
    this.form = this.#fb.group({
      emailSubscriptionEvents: [this.#locationAdminStore.emailSubscriptionEvents(), Validators.required],
      emailSubscriptionRecipients: [
        this.#locationAdminStore.emailSubscriptionRecipients(),
        [Validators.required, EmailValidator()]
      ]
    });
  }

  toggleEvent(checked: boolean, event: string): void {
    checked
      ? this.emailSubscriptionEvents.setValue([...this.emailSubscriptionEvents.value, event])
      : this.emailSubscriptionEvents.setValue(this.emailSubscriptionEvents.value.filter((e) => e !== event));
  }

  subscribeToEvents(): void {
    const { emailSubscriptionEvents, emailSubscriptionRecipients } = this.form.getRawValue();
    const subscription: RepairerJobSubscription = {
      emailSubscriptionEvents,
      emailSubscriptionRecipients: emailSubscriptionEvents?.length ? emailSubscriptionRecipients : []
    };
    this.#repairerAdminService
      .updateEmailSubscription(this.#locationAdminStore.userRepairerSite().id, subscription)
      .subscribe({
        next: () => this.#messagingService.confirm('Job Notifications saved.'),
        error: () => {
          this.#messagingService.error('Could not save Job Notifications.');
          this.#dialogRef.close();
        },
        complete: () => this.#dialogRef.close(true)
      });
  }

  get emailSubscriptionEvents(): FormControl<string[]> {
    return this.form.controls.emailSubscriptionEvents as FormControl<string[]>;
  }
}
